import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingAnimation from "../../Components/Loading/LoadingAnimation";
import { TrashIcon } from "@heroicons/react/24/solid";
import {
  GET_DOCUMENT_API,
  ADD_DOCUMENT_COMMENT_API,
  GET_DOCUMENT_COMMENTS_API,
  UPDATE_DOCUMENT_API,
  getExternalByUserId,
  deleteComment,
  uploadSignature,
} from "../../Constants/apiRoutes";
import { Document, Page, pdfjs } from "react-pdf";
import { HiOutlineUpload, HiOutlineDownload } from "react-icons/hi";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FileViewer from "react-file-viewer";
import { ToastContainer } from "react-toastify";
import { HiPencil } from "react-icons/hi";
import { FaPlus } from "react-icons/fa";
import { FaFileWord, FaFilePdf } from "react-icons/fa";
import AddApprovalPopup from "./ApprovalPopup";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { BsArrowUpRightCircleFill } from "react-icons/bs";
import { useExternalUser } from "../../Context/ExternalUserContext";
import { StatusUpdate } from "../../Constants/apiRoutes";
import { useConfirmation } from "../../Components/ConfirmDialog";
import SignatureDialog from "./SignatureDialog";
import { v4 as uuidv4 } from "uuid";

const ProjectStatus = [
  { id: 1, name: "Project Initiation" },
  { id: 2, name: "Project Planning" },
  { id: 3, name: "Project Execution" },
  { id: 4, name: "Project Control" },
  { id: 5, name: "Project Close" },
];
// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
const A4_WIDTH = 800;
const A4_HEIGHT = 990;
const DocumentsDetails = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const pdfContainerRef = useRef(null);
  const [fileType, setFileType] = useState(null);
  const [documentComments, setDocumentComments] = useState([]);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedProjectType, setSelectedProjectType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { externalUserid, documentId } = useParams(); // ✅ Get both params
  const documentNo = documentData?.DocumentNo || ""; // Ensure it exists
  const [markedPage, setMarkedPage] = useState(null);
  const { showConfirmation, ConfirmationDialog } = useConfirmation();
  const documentContainerRef = useRef(null);
  const [tempSignatures, setTempSignatures] = useState([]);
  const [modifiedDocument, setModifiedDocument] = useState(null);
  const [isMarkingPosition, setIsMarkingPosition] = useState(false);
  const [markedPosition, setMarkedPosition] = useState(null);
  const [error, setError] = useState(null);
  const [externalData, setExternalData] = useState(null);
  const { externalUser } = useExternalUser();
  const [showPopup, setShowPopup] = useState(false);
  const [approvalData, setApprovalData] = useState({ Approvals: [] });
  const [canSign, setCanSign] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isDocumentSigned, setIsDocumentSigned] = useState(false);
  const [originalPdfUrl, setOriginalPdfUrl] = useState(pdfUrl);

  useEffect(() => {
    if (
      externalUser &&
      externalUser.StatusCode === "ERROR" &&
      externalUser.message === "User not found"
    ) {
      setShowPopup(true);
    } else {
      setShowPopup(false);
    }
  }, [externalUser]);
  const pdfOptions = useMemo(
    () => ({
      cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/",
      cMapPacked: true,
      standardFontDataUrl:
        "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/",
    }),
    []
  );
  useEffect(() => {
    if (externalUserid) {
      localStorage.setItem("externalUserid", externalUserid);
    }
  }, [externalUserid]);
  useEffect(() => {
    const fetchExternalDetails = async () => {
      try {
        const response = await axios.get(
          `${getExternalByUserId}/${externalUserid}`
        );
        console.log("externalUserid", externalUserid);
        setExternalData(response.data);
      } catch (err) {
        setError("Failed to fetch external details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchExternalDetails();
  }, [externalUserid]);

  const fetchDocumentData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${GET_DOCUMENT_API}/${documentId}/${externalUserid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data?.data;

      if (data) {
        setDocumentData(data);

        setApprovalData((prev) => ({
          ...prev,
          Approvals: data.Approvals || [],
        }));

        // Check if the external user has already approved
        const currentUserApproval = data.Approvals?.find(
          (approval) => approval.UserID === Number(externalUserid)
        );

        if (currentUserApproval?.Status === "approved") {
          navigate("/thank-you", {
            state: {
              message: "You have already approved this document!",
              status: "approved",
            },
          });
          return;
        }

        // Check if external user (externalUserid) needs to sign
        const hasRequiredSignature = data.Approvals?.some((approval) => {
          return (
            approval.UserID === Number(externalUserid) &&
            approval.RequiresSignature === true
          );
        });

        setCanSign(hasRequiredSignature);
      } else {
        toast.error("Document not found");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
      toast.error(error.response?.data?.message || "Failed to fetch document");
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyles = (statusId) => {
    switch (statusId) {
      case 1:
        return {
          text: "text-yellow-600",
          dot: "bg-yellow-400",
        };
      case 2:
        return {
          text: "text-green-600",
          dot: "bg-green-500",
        };
      case 3:
        return {
          text: "text-red-600",
          dot: "bg-red-500",
        };
          case 4:
        return {
          text: "text-red-600",
          dot: "bg-red-500",
        };
      default:
        return {
          text: "text-gray-600",
          dot: "bg-gray-400",
        };
    }
  };
  const { text, dot } = getStatusStyles(documentData?.StatusId);
  const fetchDocumentComments = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${GET_DOCUMENT_COMMENTS_API}/${documentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setDocumentComments(response.data || []);
    } catch (error) {
      console.error("Error fetching document comments", error);
    }
  };
  // Fetch document data
  useEffect(() => {
    if (documentId) {
      fetchDocumentData();
      fetchDocumentComments();
    }
  }, [documentId]);

  useEffect(() => {
    if (documentData?.PublicUrl) {
      const ext = documentData.PublicUrl.split(".").pop()?.split("?")[0];
      if (ext) {
        const type = ext.toLowerCase();
        setFileType(type);
        console.log(type);
      }
    }
  }, [documentData?.PublicUrl]);

  useEffect(() => {
    if (documentData) {
      setDocumentName(documentData.DocumentName || "");
      setCustomerName(documentData.CustomerName || "");
      setSelectedStatus(documentData.StatusId || null);
      setSelectedProjectType(documentData.ProjectTypeName || null);
    }
  }, [documentData]);

  const handleAddComment = () => {
    setShowCommentModal(true);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      console.log("Trying to delete comment ID:", commentId);

      const token = localStorage.getItem("token");
      const res = await fetch(`${deleteComment}/${commentId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();
      console.log("Delete API response:", result);

      if (!res.ok) {
        throw new Error(result.message || "Failed to delete comment");
      }

      setDocumentData((prevData) => ({
        ...prevData,
        Comments: prevData.Comments.filter(
          (comment) => comment.CommentID !== commentId
        ),
      }));

      toast.success("Comment deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error deleting comment");
    }
  };
  // Child (reply) comment delete
  const handleDeleteReply = async (replyId) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${deleteComment}/${replyId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error("Failed to delete reply");
      }

      // Remove the deleted reply from the state
      setDocumentData((prevData) => ({
        ...prevData,
        Comments: prevData.Comments.filter(
          (comment) => comment.CommentID !== replyId
        ),
      }));

      toast.success("Reply deleted successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error deleting reply");
    }
  };

  const handleAddSignature = () => {
    if (fileType === "pdf" || fileType === "docx") {
      setIsMarkingPosition(true);
    } else {
      toast.error("Unsupported file format for digital signature");
    }
  };

  const handleSaveSignature = async (signatureData) => {
    if (!markedPosition || markedPage === null) return;

  const tenantId = Number(documentData?.TenantId);

    const adjustedPosition = {
      x: markedPosition.x,
      y: markedPosition.y,
    };

    const newSignature = {
      id: uuidv4(),
      type: signatureData.type,
      data: signatureData.data,
      position: adjustedPosition,
      pageIndex: markedPage,
      size: { width: 150, height: 30 },
      ...(signatureData.style && { style: signatureData.style }),
    };

    try {
      // Convert base64 image to PNG Blob
      const base64Data = signatureData.data.split(",")[1];
      const binaryData = atob(base64Data);
      const byteArray = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        byteArray[i] = binaryData.charCodeAt(i);
      }
      const imageBlob = new Blob([byteArray], { type: "image/png" });

      // Prepare FormData
      const sigForm = new FormData();
      sigForm.append("file", imageBlob, "signature.png");
      sigForm.append("documentId", documentId);
      sigForm.append("tenantId", tenantId);
      sigForm.append("createdBy", externalUserid);
      sigForm.append("userId", externalUserid);
      sigForm.append("left", adjustedPosition.x.toString());
      sigForm.append("top", adjustedPosition.y.toString());
      sigForm.append("width", "150");
      sigForm.append("height", "30");

      // 1. POST the signature image with metadata
      await axios.post(uploadSignature, sigForm, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // 2. Update Signature State
      setSignatures((prev) => {
        const updated = [
          ...prev.filter(
            (sig) =>
              !(
                sig.pageIndex === markedPage &&
                Math.abs(sig.position.x - adjustedPosition.x) < 5 &&
                Math.abs(sig.position.y - adjustedPosition.y) < 5
              )
          ),
          newSignature, // add new one
        ];
        setIsDocumentSigned(true);
        console.log("set", isDocumentSigned);
        setDocumentData((prevData) => ({
          ...prevData,
          Signatures: JSON.stringify(updated),
        }));

        return updated;
      });

      // 3. Modify the PDF visually
      const blob = await fetch(originalPdfUrl).then((res) => res.blob());
      const arrayBuffer = await blob.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const page = pdfDoc.getPages()[markedPage - 1];

      page.drawRectangle({
        x: (adjustedPosition.x / 100) * page.getWidth() - 75,
        y: (adjustedPosition.y / 100) * page.getHeight() - 15,
        width: 150,
        height: 30,
        color: rgb(1, 1, 1),
      });

      const imageBytes = await fetch(signatureData.data).then((res) =>
        res.arrayBuffer()
      );
      const pngImage = await pdfDoc.embedPng(imageBytes);
      page.drawImage(pngImage, {
        x: (adjustedPosition.x / 100) * page.getWidth() - 75,
        y: (adjustedPosition.y / 100) * page.getHeight() - 15,
        width: 150,
        height: 30,
      });

      // 4. Update preview
      const updatedPdfBytes = await pdfDoc.save();
      const updatedBlob = new Blob([updatedPdfBytes], {
        type: "application/pdf",
      });
      const updatedPdfUrl = URL.createObjectURL(updatedBlob);
      setPdfUrl(updatedPdfUrl);
      setOriginalPdfUrl(updatedPdfUrl);
    } catch (err) {
      console.error("Signature error:", err);
    }
    console.log("set", isDocumentSigned);
    setIsDocumentSigned(true);
    setMarkedPosition(null);
    setMarkedPage(null);
    setShowSignatureDialog(false);
  };

  const [replyTexts, setReplyTexts] = useState("");
  const [replyingTo, setReplyingTo] = useState(null); // track comment being replied to

  const handleReplySubmit = async (commentID) => {
    const reply = replyTexts[commentID];
    if (!reply?.trim()) {
      toast.error("Please enter a reply");
      return;
    }

    const token = localStorage.getItem("token");
 
  const tenantId = Number(documentData?.TenantId);

    try {
      const response = await axios.post(
        ADD_DOCUMENT_COMMENT_API,
        {
          DocumentID: documentId,
          UserID: externalUserid,
          CommentText: reply,
          TenantId: tenantId,
          ParentCommentID: commentID,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success("Reply added successfully");
        setReplyTexts((prev) => ({ ...prev, [commentID]: "" }));
        setReplyingTo(null);
        setTimeout(() => {
          fetchDocumentData();
        }, 1000); // 1 second is usually enough
      } else {
        toast.error(response.data.message || "Failed to add reply");
      }
    } catch (error) {
      console.error("Failed to add sub-comment", error);
      toast.error("Failed to add reply");
    }
  };

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  const handleDownload = async () => {
    if (documentData?.PublicUrl) {
      try {
        const response = await fetch(documentData.PublicUrl, {
          mode: "cors",
        });
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute(
          "download",
          documentData.DocumentName || "document.pdf"
        ); // Set the filename
        document.body.appendChild(link);
        link.click(); // Programmatically click the link to trigger the download
        document.body.removeChild(link); // Clean up
        window.URL.revokeObjectURL(blobUrl); // Release the blob URL
        toast.success("Download started"); // Optional: Show a success message
      } catch (error) {
        console.error("Error downloading file:", error);
        toast.error("Failed to download file"); // Optional: Show an error message
      }
    } else {
      toast.error("File not found"); // Optional: Show an error message if filePath is invalid
    }
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setIsSubmittingComment(true);
   
  const tenantId = Number(documentData?.TenantId);
    try {
      const response = await axios.post(
        ADD_DOCUMENT_COMMENT_API,
        {
          DocumentID: documentId,
          UserID:externalUserid,
          CommentText: commentText,
          TenantId: tenantId,
          ParentCommentID: 0,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success("Comment added successfully");
        setCommentText("");
        setShowCommentModal(false);
        setTimeout(() => {
          fetchDocumentData();
        }, 1000); // 1 second is usually enough
      } else {
        toast.error(response.data.message || "Failed to add comment");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleUpdateDocument = async () => {
    setIsLoading(true);

    const tenantId = Number(documentData?.TenantId);


    if (!documentData || !documentData.DocumentNo) {
      toast.error("Invalid document data.");
      setIsLoading(false);
      return;
    }

    try {
      const pdfUrl = documentData.PublicUrl.includes("?")
        ? `${documentData.PublicUrl}&t=${Date.now()}`
        : `${documentData.PublicUrl}?t=${Date.now()}`;

      const pdfResponse = await fetch(pdfUrl, {
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
      });

      if (!pdfResponse.ok) {
        throw new Error(
          `Failed to fetch PDF (${pdfResponse.status}): ${pdfResponse.statusText}`
        );
      }

      const pdfBytes = await pdfResponse.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes, {
        ignoreEncryption: false,
        updateMetadata: false,
      });

      if (pdfDoc.isEncrypted) {
        throw new Error("PDF is encrypted/cannot be modified");
      }

      const pages = pdfDoc.getPages();
      if (pages.length === 0) throw new Error("PDF contains no pages");

      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

      if (isMarkingPosition && markedPosition && markedPage != null) {
        const pageIndex = markedPage - 1;
        if (pageIndex >= 0 && pageIndex < pages.length) {
          const page = pages[pageIndex];
          const { width, height } = page.getSize();

          const markerWidth = 150;
          const markerHeight = 50;
          const pdfX = (markedPosition.x / 100) * width;
          const pdfY = height - (markedPosition.y / 100) * height;

          const finalX = Math.max(
            0,
            Math.min(width - markerWidth, pdfX - markerWidth / 2)
          );
          const finalY = Math.max(
            0,
            Math.min(height - markerHeight, pdfY - markerHeight / 30)
          );

          const borderBlue400 = rgb(96 / 255, 165 / 255, 250 / 255);

          page.drawRectangle({
            x: finalX,
            y: finalY,
            width: markerWidth,
            height: markerHeight,
            borderWidth: 1,
            borderColor: borderBlue400,
          });

          const hasSignature = signatures.some(
            (sig) =>
              sig.pageIndex === markedPage &&
              sig.position.x === markedPosition.x &&
              sig.position.y === markedPosition.y
          );

          if (!hasSignature) {
            const textBlue500 = rgb(59 / 255, 130 / 255, 246 / 255);
            page.drawText("Signature", {
              x: finalX + markerWidth / 2 - 30,
              y: finalY + markerHeight / 12,
              size: 12,
              font,
              color: textBlue500,
            });
          }
        }
      }

      const signaturesByPage = {};
      signatures.forEach((sig) => {
        const idx = (sig.pageIndex ?? 1) - 1;
        if (!signaturesByPage[idx]) signaturesByPage[idx] = [];
        signaturesByPage[idx].push(sig);
      });

      for (const [pageIndexStr, pageSignatures] of Object.entries(
        signaturesByPage
      )) {
        const pageIndex = parseInt(pageIndexStr);
        if (pageIndex < 0 || pageIndex >= pages.length) continue;

        const page = pages[pageIndex];
        const { width, height } = page.getSize();

        await Promise.all(
          pageSignatures.map(async (sig) => {
            try {
              const { type, data, position, style } = sig;

              if (type === "draw" || type === "image") {
                let imageData;

                if (type === "draw") {
                  if (!data || !data.includes("base64"))
                    throw new Error("Invalid data format");
                  const base64 = data.split(",")[1];
                  imageData = Uint8Array.from(atob(base64), (c) =>
                    c.charCodeAt(0)
                  );
                } else {
                  const imgRes = await fetch(data);
                  if (!imgRes.ok) throw new Error("Failed to fetch image");
                  imageData = await imgRes.arrayBuffer();
                }

                let image;
                try {
                  image = await pdfDoc.embedPng(imageData);
                } catch {
                  image = await pdfDoc.embedJpg(imageData);
                }

                const scale = 0.25; // increased from 0.2
                const maxWidth = 150; // matching the UI
                const maxHeight = 30;

                let imgWidth = image.width;
                let imgHeight = image.height;

                if (imgWidth > maxWidth) {
                  const r = maxWidth / imgWidth;
                  imgWidth = maxWidth;
                  imgHeight *= r;
                }

                if (imgHeight > maxHeight) {
                  const r = maxHeight / imgHeight;
                  imgHeight = maxHeight;
                  imgWidth *= r;
                }

                const x = Math.max(
                  0,
                  Math.min(width - imgWidth, (position.x / 100) * width - 40)
                );
              const y = Math.max(
  0,
  Math.min(
    height - imgHeight,
    height - (position.y / 100) * height - imgHeight / 2-5
  )
);


                page.drawImage(image, {
                  x,
                  y,
                  width: imgWidth,
                  height: imgHeight,
                });
              } else if (type === "text") {
                const fontSize = style?.fontSize || 10;
                const x = (position.x / 100) * width;
                const y = height - (position.y / 100) * height;
                page.drawText(data, {
                  x: x - 30,
                  y: y + 15,
                  size: fontSize,
                  font,
                  color: rgb(0, 0, 0),
                });
              }
            } catch (err) {
              console.error("Signature error:", err.message);
            }
          })
        );
      }

      const modifiedPdfBytes = await pdfDoc.save();

      const formData = new FormData();
      // formData.append("DocumentNo", documentData.DocumentNo);
      formData.append("DepartmentId", documentData.DepartmentId || "");
      formData.append("DocumentName", documentData.DocumentName || "");
      formData.append("CustomerName", documentData.CustomerName || "");
      formData.append("ProjectPhaseId", documentData.ProjectPhaseId || "");
      formData.append("StatusId", Number(documentData.StatusId) || 0);
      formData.append("ProjectTypeId",Number(documentData.ProjectTypeName) || 1);
      formData.append("UpdatedBy", externalUserid || 0);
      formData.append("TenantId", tenantId);

      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      formData.append("UploadDocument", blob, "modified-document.pdf");

      const response = await axios.put(
        `${UPDATE_DOCUMENT_API}/${documentId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === "success") {
        toast.success("Document updated successfully!");
        setIsMarkingPosition(false);
        setSignatures([]);
        setIsDocumentSigned(true);
        setDocumentData((prevData) => ({
          ...prevData,
          Signatures: JSON.stringify([]),
        }));
        setTimeout(() => {
          fetchDocumentData();
        }, 1000);
      } else {
        toast.error(response.data.message || "Failed to update document");
      }
    } catch (error) {
      console.error("Update failed:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignatureComplete = async (signatureData) => {
    try {
      const newSignature = {
        id: Date.now(),
        image: signatureData.signatureImage,
        position: signatureData.metadata.position,
        signedBy: signatureData.metadata.signedBy,
        signedDate: signatureData.metadata.signedDate,
      };

      // Add the new signature to the temporary signatures array
      setTempSignatures((prev) => [...prev, newSignature]);

      // If it's a PDF, immediately process the signature
      if (fileType === "pdf") {
        const documentResponse = await fetch(documentData.PublicUrl);
        const pdfBytes = await documentResponse.arrayBuffer();

        // Load the PDF document
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const pages = pdfDoc.getPages();
        const page = pages[0]; // You might want to handle multiple pages

        // Convert signature to proper format
        const signatureImageData = signatureData.signatureImage.split(",")[1];
        const signatureBytes = Uint8Array.from(atob(signatureImageData), (c) =>
          c.charCodeAt(0)
        );

        // Embed the signature image
        const signatureImage = await pdfDoc.embedPng(signatureBytes);

        // Calculate position
        const { width, height } = page.getSize();
        const xPos = (signatureData.metadata.position.x / 100) * width;
        const yPos =
          height - (signatureData.metadata.position.y / 100) * height;

        // Draw the signature
        page.drawImage(signatureImage, {
          x: xPos,
          y: yPos,
          width: 200,
          height: 100,
        });

        // Save the modified PDF
        const modifiedPdfBytes = await pdfDoc.save();
        const modifiedPdfBlob = new Blob([modifiedPdfBytes], {
          type: "application/pdf",
        });

        setModifiedDocument(modifiedPdfBlob);
      }

      toast.success("Signature added successfully");
    } catch (error) {
      console.error("Error processing signature:", error);
      toast.error("Failed to process signature");
    }
  };

  const clickTimerRef = useRef(null);
  const handleDocumentClick = (e, pageIndex) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      return; // Stop further execution if double-click cleared the timer
    }

    // Set the timer for single click
    const pageElement = e.currentTarget;

    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      console.log(isMarkingPosition);
      // Only handle position marking when not double-clicking
      if (isMarkingPosition && pageElement) {
        const rect = pageElement.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;
        const x = (clickX / rect.width) * 100;
        const y = (clickY / rect.height) * 100;

        setMarkedPosition({ x, y });
        setMarkedPage(pageIndex);
        setIsMarkingPosition(true);
      }
    }, 250); // Delay for single click
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "text-yellow-600";
      case 2:
        return "text-green-600";
      case 3:
        return "text-red-600";
      case 4:
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };
  const renderDocumentPreview = () => {
    if (!documentData?.PublicUrl) return null;

    const handleApplySignature = () => {
      if (markedPosition && markedPage) {
        setShowSignatureDialog(true);
      }
    };

    return (
      <div
        className="relative bg-gray-100"
        ref={documentContainerRef}
        style={{ cursor: isMarkingPosition ? "crosshair" : "default" }}
      >
        <div className="document-container">
          {fileType === "pdf" ? (
            <Document
              file={documentData.PublicUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<LoadingAnimation />}
            >
              {Array.from(new Array(numPages), (el, index) => (
                <div
                  key={`page_${index + 1}`}
                  className="relative mx-auto mb-10 shadow-xl bg-white border border-gray-300"
                  style={{
                    width: `${A4_WIDTH * scale}px`,
                    cursor: isMarkingPosition ? "crosshair" : "default",
                    position: "relative",
                  }}
                  onClick={(e) => handleDocumentClick(e, index + 1)}
                >
                  <Page
                    pageNumber={index + 1}
                    scale={scale}
                    width={A4_WIDTH * scale}
                    className="w-full h-full"
                  />

                  {isMarkingPosition &&
                    markedPosition &&
                    markedPage === index + 1 &&
                    !signatures.some((sig) => sig.pageIndex === markedPage) && (
                      <div
                        className="absolute border-4 border-black bg-opacity-30 w-[200px] h-[70px] rounded-xl"
                        style={{
                          left: `${markedPosition.x}%`,
                          top: `${markedPosition.y}%`,
                          transform: "translate(-50%, -50%)",
                          pointerEvents: "auto",
                        }}
                      >
                        <div className="text-center text-black mt-10 font-sans">
                          Signature Placeholder
                        </div>
                      </div>
                    )}

                  {signatures
                    .filter((signature) => signature.pageIndex === index + 1)
                    .map((signature) => (
                      <div
                        key={signature.id}
                        className="absolute group"
                        style={{
                          left: `${signature.position.x}%`,
                          top: `${signature.position.y + 1}%`,
                          transform: `translate(-50%, -50%)`,
                        }}
                      >
                        {signature.type === "draw" ||
                        signature.type === "image" ? (
                          <img
                            src={signature.data}
                            alt="signature"
                            className="max-w-[200px] max-h-[35px]"
                            style={{ transform: `scale(${scale})` }}
                          />
                        ) : (
                          <div
                            className="text-signature"
                            style={{
                              fontFamily:
                                signature.style?.fontFamily || "cursive",
                              fontSize: `${signature.style?.fontSize || 20}px`,
                              color: signature.style?.color || "#000000",
                              transform: `scale(${scale})`,
                            }}
                          >
                            {signature.data}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ))}
            </Document>
          ) : (
            <div
              className="relative"
              ref={documentContainerRef}
              onClick={(e) => handleDocumentClick(e, 1)} // Hardcoded pageIndex for DOCX
              style={{ cursor: isMarkingPosition ? "crosshair" : "default" }}
            >
              <FileViewer
                fileType={fileType}
                filePath={documentData.PublicUrl}
              />

              {isMarkingPosition && markedPosition && markedPage === 1 && (
                <div
                  className="absolute border-2 border-blue-400 bg-opacity-30 w-[200px] h-[70px]"
                  style={{
                    left: `${markedPosition.x}%`,
                    top: `${markedPosition.y}%`,
                    transform: "translate(-50%, -50%)",
                    pointerEvents: "auto",
                  }}
                >
                  <div className="text-center text-blue-500 mt-10 font-sans">
                    Signature
                  </div>
                </div>
              )}

              {signatures
                .filter((signature) => signature.pageIndex === 1)
                .map((signature) => (
                  <div
                    key={signature.id}
                    className="absolute group"
                    style={{
                      left: `${signature.position.x}%`,
                      top: `${signature.position.y}%`,
                      transform: `translate(-50%, -50%)`,
                    }}
                  >
                    {signature.type === "draw" || signature.type === "image" ? (
                      <img
                        src={signature.data}
                        alt="signature"
                        className="max-w-[200px] max-h-[100px]"
                        style={{ transform: `scale(${scale})` }}
                      />
                    ) : (
                      <div
                        className="text-signature"
                        style={{
                          fontFamily: signature.style?.fontFamily || "cursive",
                          fontSize: `${signature.style?.fontSize || 20}px`,
                          color: signature.style?.color || "#000000",
                          transform: `scale(${scale})`,
                        }}
                      >
                        {signature.data}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>

        {isMarkingPosition && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-lg z-50">
            Click where you want to place the signature
          </div>
        )}

        {/* Add Apply Signature button when a position is marked */}
        {isMarkingPosition && markedPosition && (
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-lg z-50">
            <button
              className="bg-[#8B4513] text-white px-4 py-2 rounded"
              onClick={handleApplySignature}
            >
              Select Signature
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleStatusUpdate = (status) => {
    // Only show missing signature warning if approving (not declining)
    if (status === "approved" && !isDocumentSigned && canSign) {
      showConfirmation(
        "Missing Signature",
        "Please add a signature on the document before approving.",
        () => {}, // No-op
        true // suppressToast = true
      );
      return;
    }

    // Proceed with confirmation based on status
    showConfirmation(
      `Confirm ${status}`,
      `Are you sure you want to ${status} this document?`,
      async () => {
        // Async callback to handle the update process
        try {
          setIsLoading(true);

          // Only call handleUpdateDocument if approving and signed
          if (status === "approved" && isDocumentSigned) {
            await handleUpdateDocument();
          }

     const tenantId = Number(documentData?.TenantId);
          const payload = {
            Status: status,
            TenantId: Number(tenantId),
            Reinitiate:false, 
          };

          // Update the document status
          await axios.put(
            `${StatusUpdate}/${documentId}/user/${externalUserid}`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          // After a successful update, navigate with a delay
          navigate("/thank-you", {
            state: {
              message: `Document has been ${status} successfully!`,
              status: status,
            },
          });
        } catch (error) {
          console.error(`Error updating status to "${status}":`, error);
       const errorMessage =
        error?.response?.data?.error || 
        error?.message ||               
        `Failed to ${status} document`; 
      toast.error(errorMessage);
          setIsLoading(false);
        }
      },
      true // This ensures the toast is suppressed
    );
  };
  return (
    <div>
      {!showPopup && (
        <div className="flex h-screen bg-white mt-10">
          {/* Main Content */}
          <ToastContainer />
          <ConfirmationDialog />
          <div className="flex-1 flex flex-col">
            {/* Top Bar */}
            <div className="border-b p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="text-lg font-medium flex items-center">
                  {fileType === "pdf" ? (
                    <FaFilePdf className="w-5 h-5 text-red-500 mr-2" />
                  ) : fileType === "docx" ? (
                    <FaFileWord className="w-5 h-5 text-blue-600 mr-2" />
                  ) : null}
                  {documentData?.DocumentName || "Document Details"}
                </h1>
                <span className="text-gray-500 text-sm">
                  Document No: {documentData?.DocumentNo}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-4">
                  {(fileType === "pdf" || fileType === "docx") && (
                    <button
                      onClick={handleAddSignature}
                      disabled={!canSign}
                      className={`px-4 py-2 rounded flex items-center text-white bg-[#8B4513]
                                  ${
                                    canSign
                                      ? "bg-[#8B4513] hover:bg-[#8B4513]"
                                      : "bg-[#8B4513] opacity-50"
                                  }`}
                    >
                      <HiPencil className="w-4 h-4 mr-2" />
                      Add Signature
                    </button>
                  )}

                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      {/* Dot */}
                      <span className={`w-2 h-2 rounded-full ${dot}`}></span>

                      {/* Status Text */}
                      <p className={`text-sm font-medium ${text}`}>
                        {documentData?.StatusId === 1
                          ? "Pending"
                          : documentData?.StatusId === 2
                          ? "Approved"
                          : documentData?.StatusId === 3
                          ? "Rejected"
                           : documentData?.StatusId === 4
                          ? "Declined"
                          : "Unknown"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Document Content */}
            <div className="flex-1 p-8 overflow-hidden">
              <div className="h-full flex flex-col">
                {/* Document Details */}
                <div className="mb-4 bg-white rounded-lg shadow-sm p-4">
                  <div className="relative">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Document No</p>
                        <p className="font-medium">
                          {documentData?.DocumentNo}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-500">Customer Name</p>
                        <p className="font-medium">
                          {documentData?.CustomerName}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-500">Project Type</p>
                        <p className="font-medium">
                          {documentData?.ProjectTypeName}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          Project Type Status
                        </p>
                        <p className="font-medium">
                          {ProjectStatus.find(
                            (status) =>
                              status.id === documentData?.ProjectPhaseId
                          )?.name || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between mt-4 items-center">
                    <div>
                      <p className="text-sm text-gray-500">
                        {documentData?.UpdatedDate
                          ? "Updated Date"
                          : "Created Date"}
                      </p>
                      <p className="font-medium">
                        {new Date(
                          documentData?.UpdatedDate || documentData?.CreatedDate
                        ).toLocaleString("en-US", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </p>
                    </div>
                    {/* Buttons on the right */}
                    <div className="flex space-x-4 items-center">
                      <button
                        onClick={handleDownload}
                        className="px-4 py-2 bg-[#8B4513] text-white rounded hover:bg-opacity-90 flex items-center"
                        title="Download Document"
                      >
                        <HiOutlineDownload className="w-5 h-5" />
                      </button>
                      <button
                        onClick={handleUpdateDocument}
                        disabled={!canSign}
                        className={`px-4 py-2 rounded flex items-center text-white bg-[#8B4513]
                                         ${
                                           canSign
                                             ? "bg-[#8B4513] hover:bg-opacity-90"
                                             : "bg-[#8B4513] opacity-50"
                                         }`}
                        title="Update Document"
                      >
                        {isLoading ? (
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : (
                          <HiOutlineUpload className="w-5 h-5 text-white" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                {/* Document Preview */}
                <div
                  className="flex-1 bg-gray-100 rounded-lg shadow-sm overflow-auto relative"
                  ref={pdfContainerRef}
                >
                  {renderDocumentPreview()}
                </div>
              </div>
            </div>
          </div>
          <SignatureDialog
            showDialog={showSignatureDialog}
            setShowDialog={setShowSignatureDialog}
            onSaveSignature={handleSaveSignature}
          />
          {/*<AddApprovalPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />*/}
          <AddApprovalPopup
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(false)}
            documentId={documentId}
          />
          {/* Comment Modal */}
          {showCommentModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-[600px] max-w-[90%]">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Add Comment</h2>
                  <button
                    onClick={() => setShowCommentModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="w-full h-32 p-3 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your comment here..."
                />
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCommentModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitComment}
                    disabled={isSubmittingComment}
                    className={`px-4 py-2 bg-[#8B4513] text-white rounded hover:bg-[#8B4513] flex items-center ${
                      isSubmittingComment ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {isSubmittingComment ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      "Submit Comment"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Right Sidebar - Approvals */}
          <div className="w-80 border-l bg-gray-50">
            <div className="p-4">
              <div className="flex items-center justify-between mt-6">
                <h2 className="font-semibold mb-4 text-gray-800">Approval</h2>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:space-x-4 w-full">
                <button
                  className="bg-green-500 text-white px-6 sm:px-10 py-2 rounded hover:bg-green-600 w-full sm:w-auto"
                  onClick={() => handleStatusUpdate("approved")}
                >
                  Approve
                </button>

                <button
                  className="bg-red-500 text-white px-6 sm:px-10 py-2 rounded hover:bg-red-600 w-full sm:w-auto"
                  onClick={() => handleStatusUpdate("declined")}
                >
                  Decline
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mt-6 mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Comments
                </h2>
                <button
                  onClick={handleAddComment}
                  className="px-4 py-2 bg-[#8B4513] text-white rounded hover:bg-opacity-90 flex items-center"
                >
                  <FaPlus className="w-4 h-4" />
                </button>
              </div>

              <div className="max-h-[300px] overflow-y-auto pr-2 space-y-5">
                {documentData?.Comments?.length > 0 ? (
                  documentData.Comments.filter(
                    (comment) => comment.ParentCommentID === null
                  ).map((parentComment) => {
                    const replies = documentData.Comments.filter(
                      (reply) =>
                        reply.ParentCommentID === parentComment.CommentID
                    );

                    return (
                      <div
                        key={parentComment.CommentID}
                        className="bg-white p-4 rounded-lg shadow-sm border"
                      >
                        {/* Parent comment */}
                        <div className="flex justify-between items-start mb-1">
                          <div>
                            <p className="text-base font-medium text-gray-800">
                              {parentComment.UserName || "Unknown User"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(
                                parentComment.CreatedDate
                              ).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() =>
                                setReplyingTo(
                                  replyingTo === parentComment.CommentID
                                    ? null
                                    : parentComment.CommentID
                                )
                              }
                              className="text-blue-600 text-sm hover:underline"
                            >
                              Reply
                            </button>
                         
                          </div>
                        </div>

                        <p className="text-sm text-gray-700 mt-2">
                          {parentComment.CommentText}
                        </p>

                        {/* Reply input */}
                        {replyingTo === parentComment.CommentID && (
                          <div className="mt-3 relative max-w-md">
                            <input
                              type="text"
                              value={replyTexts[parentComment.CommentID] || ""}
                              onChange={(e) =>
                                setReplyTexts({
                                  ...replyTexts,
                                  [parentComment.CommentID]: e.target.value,
                                })
                              }
                              placeholder="Write a reply..."
                              className="w-full pr-10 px-3 py-2 border rounded text-sm"
                            />
                            <button
                              onClick={() =>
                                handleReplySubmit(parentComment.CommentID)
                              }
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8B4513] hover:text-[#A0522D]"
                            >
                              <BsArrowUpRightCircleFill size={20} />
                            </button>
                          </div>
                        )}
                        {/* Replies */}
                        {replies.length > 0 && (
                          <div className="mt-4 space-y-3 pl-4 border-l-2 border-gray-200">
                            {replies.map((reply) => (
                              <div
                                key={reply.CommentID}
                                className="bg-gray-100 p-3 rounded-lg shadow-sm"
                              >
                                <div className="flex justify-between items-start mb-1">
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">
                                      {reply.UserName || "Unknown User"}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(
                                        reply.CreatedDate
                                      ).toLocaleString()}
                                    </p>
                                  </div>
                              
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {reply.CommentText}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-600">
                    No comments available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Popup */}
      {showPopup && (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg">
            <h2 className="text-red-600 text-lg font-semibold">
              User Not Found
            </h2>
            <p className="mt-2">{externalUser.message}</p>
            <button
              onClick={() => setShowPopup(false)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsDetails;
